import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Eye, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BookingAccountingPanel — v2.5.1
 *
 * Each field saves independently via PATCH /bookings/{id}/accounting
 * with ONLY that single field in the payload.
 *
 * This prevents payment_mode from being reset to "AMEX B" when you
 * change payment_status, and vice versa.
 */
export default function BookingAccountingPanel({ booking, onUpdate }) {
  const { getAuthHeaders } = useAuth();

  // Initialise from booking.accounting or sensible defaults
  const acc = booking?.accounting || {};
  const [fields, setFields] = useState({
    invoice_number:     acc.invoice_number     || "",
    invoice_date:       acc.invoice_date       || "",
    item_type:          acc.item_type          || "Air Transport Srvc (flight tickets)",
    vat_type:           acc.vat_type           || "0% VAT",
    quarter:            acc.quarter            || "",
    supplier_name:      acc.supplier_name      || "",
    supplier_reference: acc.supplier_reference || "",
    payment_mode:       acc.payment_mode       || "",
    supplier_paid_date: acc.supplier_paid_date || "",
    payment_status:     acc.payment_status     || "Paid",
    date_received:      acc.date_received      || "",
    amount_received:    acc.amount_received    != null ? String(acc.amount_received) : "",
    bank_fee:           acc.bank_fee           != null ? String(acc.bank_fee)        : "0",
    comment:            acc.comment            || "",
  });

  const [saving, setSaving] = useState({});
  const [uploading, setUploading] = useState(false);
  const receiptUrl = acc.supplier_receipt_url || null;

  // Re-sync if booking prop changes
  useEffect(() => {
    const a = booking?.accounting || {};
    setFields({
      invoice_number:     a.invoice_number     || "",
      invoice_date:       a.invoice_date       || "",
      item_type:          a.item_type          || "Air Transport Srvc (flight tickets)",
      vat_type:           a.vat_type           || "0% VAT",
      quarter:            a.quarter            || "",
      supplier_name:      a.supplier_name      || "",
      supplier_reference: a.supplier_reference || "",
      payment_mode:       a.payment_mode       || "",
      supplier_paid_date: a.supplier_paid_date || "",
      payment_status:     a.payment_status     || "Paid",
      date_received:      a.date_received      || "",
      amount_received:    a.amount_received    != null ? String(a.amount_received) : "",
      bank_fee:           a.bank_fee           != null ? String(a.bank_fee)        : "0",
      comment:            a.comment            || "",
    });
  }, [booking]);

  // ── Save a single field ────────────────────────────────────────────────────
  const saveField = async (fieldName, value) => {
    setSaving(s => ({ ...s, [fieldName]: true }));
    try {
      // Build a minimal payload — ONLY the field being saved
      const payload = { [fieldName]: value };
      // Auto-derive quarter when invoice_date changes
      if (fieldName === "invoice_date" && value) {
        const m = new Date(value).getMonth();
        payload.quarter = `Q${Math.floor(m / 3) + 1}`;
        setFields(f => ({ ...f, quarter: payload.quarter }));
      }
      await axios.patch(
        `${API}/bookings/${booking.id}/accounting`,
        payload,
        { headers: getAuthHeaders() }
      );
      toast.success("Saved ✓");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Save failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(s => ({ ...s, [fieldName]: false }));
    }
  };

  // ── Receipt upload ────────────────────────────────────────────────────────
  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(",")[1];
        const ext = file.name.split(".").pop() || "pdf";
        const invNum = fields.invoice_number || booking.id;
        const res = await axios.post(`${API}/uploads/receipt`, {
          file_data: base64,
          filename: `${invNum}.${ext}`,
          mime_type: file.type,
          folder: "supplier-receipts",
          invoice_number: invNum,
        }, { headers: getAuthHeaders() });
        if (res.data?.url) {
          await axios.post(`${API}/accounting/attach-receipt`, {
            invoice_number: invNum,
            supplier_receipt_url: res.data.url,
          }, { headers: getAuthHeaders() });
          toast.success("Receipt uploaded ✓");
          if (onUpdate) onUpdate();
        }
      } catch (err) {
        toast.error("Upload failed");
      } finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  // ── Shared field change handler ────────────────────────────────────────────
  const handleChange = (field, value) => setFields(f => ({ ...f, [field]: value }));

  // ── Blur-to-save for text inputs ────────────────────────────────────────────
  const handleBlur = (field) => {
    const value = fields[field];
    const original = (booking?.accounting || {})[field];
    if (String(value ?? "") !== String(original ?? "")) {
      saveField(field, value);
    }
  };

  // ── Immediate save for selects ────────────────────────────────────────────
  const handleSelectChange = (field, value) => {
    setFields(f => ({ ...f, [field]: value }));
    saveField(field, value);
  };

  const tf = "w-full h-9 text-sm border rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-purple-400";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {/* Invoice Number */}
        <div>
          <Label className="text-xs text-slate-500">Invoice Number</Label>
          <Input value={fields.invoice_number} readOnly className="mt-1 h-9 bg-slate-50 font-mono text-sm" />
        </div>

        {/* Invoice Date */}
        <div>
          <Label className="text-xs text-slate-500">Invoice Date</Label>
          <Input type="date" value={fields.invoice_date}
            onChange={e => handleChange("invoice_date", e.target.value)}
            onBlur={() => handleBlur("invoice_date")}
            className="mt-1 h-9 text-sm" />
        </div>

        {/* VAT Type */}
        <div>
          <Label className="text-xs text-slate-500">VAT Type</Label>
          <Select value={fields.vat_type} onValueChange={v => handleSelectChange("vat_type", v)}>
            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["0% VAT","Outside Scope EU VAT","21% VAT","9% VAT"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Quarter */}
        <div>
          <Label className="text-xs text-slate-500">Quarter</Label>
          <Select value={fields.quarter} onValueChange={v => handleSelectChange("quarter", v)}>
            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Auto" /></SelectTrigger>
            <SelectContent>
              {["Q1","Q2","Q3","Q4"].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Supplier Name */}
        <div>
          <Label className="text-xs text-slate-500">Supplier Name</Label>
          <Input value={fields.supplier_name} placeholder="trip.com, budgetair.nl…"
            onChange={e => handleChange("supplier_name", e.target.value)}
            onBlur={() => handleBlur("supplier_name")}
            className="mt-1 h-9 text-sm" />
        </div>

        {/* Supplier Reference */}
        <div>
          <Label className="text-xs text-slate-500">Supplier Reference #</Label>
          <Input value={fields.supplier_reference} placeholder="OTA order number"
            onChange={e => handleChange("supplier_reference", e.target.value)}
            onBlur={() => handleBlur("supplier_reference")}
            className="mt-1 h-9 text-sm font-mono" />
        </div>

        {/* Payment Mode — free text, saves independently */}
        <div>
          <Label className="text-xs text-slate-500">Paid With</Label>
          <Input value={fields.payment_mode} placeholder="AMEX B, Revolut, Bank Transfer…"
            onChange={e => handleChange("payment_mode", e.target.value)}
            onBlur={() => handleBlur("payment_mode")}
            className="mt-1 h-9 text-sm" />
        </div>

        {/* Supplier Paid Date */}
        <div>
          <Label className="text-xs text-slate-500">Date Paid to Supplier</Label>
          <Input type="date" value={fields.supplier_paid_date}
            onChange={e => handleChange("supplier_paid_date", e.target.value)}
            onBlur={() => handleBlur("supplier_paid_date")}
            className="mt-1 h-9 text-sm" />
        </div>

        {/* Payment Status — saves independently */}
        <div>
          <Label className="text-xs text-slate-500">Payment Status</Label>
          <Select value={fields.payment_status} onValueChange={v => handleSelectChange("payment_status", v)}>
            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Paid","Pending","Full Refund","Partially Refunded"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Date Received */}
        <div>
          <Label className="text-xs text-slate-500">Date Received from Customer</Label>
          <Input type="date" value={fields.date_received}
            onChange={e => handleChange("date_received", e.target.value)}
            onBlur={() => handleBlur("date_received")}
            className="mt-1 h-9 text-sm" />
        </div>

        {/* Amount Received */}
        <div>
          <Label className="text-xs text-slate-500">Amount Received (€)</Label>
          <Input type="number" step="0.01" value={fields.amount_received} placeholder="0.00"
            onChange={e => handleChange("amount_received", e.target.value)}
            onBlur={() => handleBlur("amount_received")}
            className="mt-1 h-9 text-sm font-mono" />
        </div>

        {/* Bank Fee */}
        <div>
          <Label className="text-xs text-slate-500">Bank Fee (€)</Label>
          <Input type="number" step="0.01" value={fields.bank_fee} placeholder="0.00"
            onChange={e => handleChange("bank_fee", e.target.value)}
            onBlur={() => handleBlur("bank_fee")}
            className="mt-1 h-9 text-sm font-mono" />
        </div>
      </div>

      {/* Item Type — full width */}
      <div>
        <Label className="text-xs text-slate-500">Item Description</Label>
        <Input value={fields.item_type}
          onChange={e => handleChange("item_type", e.target.value)}
          onBlur={() => handleBlur("item_type")}
          className="mt-1 h-9 text-sm" />
      </div>

      {/* Comment — full width */}
      <div>
        <Label className="text-xs text-slate-500">Internal Note</Label>
        <Input value={fields.comment} placeholder="Optional internal note"
          onChange={e => handleChange("comment", e.target.value)}
          onBlur={() => handleBlur("comment")}
          className="mt-1 h-9 text-sm" />
      </div>

      {/* Supplier Receipt */}
      <div>
        <Label className="text-xs text-slate-500">Supplier Payment Proof</Label>
        <div className="mt-1 flex items-center gap-2">
          {receiptUrl ? (
            <a href={receiptUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 bg-blue-50 rounded-lg px-3 py-2">
              <Eye className="w-4 h-4" /> View Receipt
            </a>
          ) : (
            <label className="flex items-center gap-2 text-sm text-slate-600 hover:text-purple-700 border border-dashed border-slate-300 hover:border-purple-400 rounded-lg px-3 py-2 cursor-pointer transition-colors">
              <Upload className="w-4 h-4" />
              {uploading ? "Uploading…" : "Upload Receipt (PDF or image)"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleReceiptUpload} disabled={uploading} />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
